import { FC, useEffect } from 'react'
import toast from 'react-hot-toast'
import InfiniteScroll from 'react-infinite-scroll-component'
import Masonry from 'react-masonry-css'
import { debounce, times, uniqBy } from 'lodash'
import { useApolloClient } from '@apollo/client'
import { generateCaptionFeed } from '../../../graphql'
import {
  changeCaptionsPage,
  changeCaptionsTotalDocs,
  setCaptions,
  setCaptionsQueryState,
  useAppDispatch,
  useAppSelector
} from '../../../redux'
import { conditionalStage, isMobile, useSearchParams, useSegment } from '../../../utils'
import { EmptyState, Skeleton } from '../..'
import { CaptionListItem } from '..'
import { StyledCaptionList } from '.'

const CaptionList: FC = () => {
  const apollo = useApolloClient()
  const dispatch = useAppDispatch()
  const { track } = useSegment()
  const { loading, captions, totalDocs, page, search, liked, categories, sort, hideUsed, caption } =
    useAppSelector((state) => state.captions)

  const { getSearchFromURL } = useSearchParams({
    search,
    categories,
    liked,
    sort,
    hideUsed,
    caption
  })

  const handleSearchTrack = debounce((resultsCount: number) => {
    track('Searched Caption Library', {
      page,
      search,
      sorted_by: sort,
      liked_filter: liked,
      used_filter: hideUsed,
      category_filter: categories,
      search_results_count: resultsCount
    })
  }, 3000)

  const loadCaptions = async () => {
    try {
      const { data } = await apollo.query({
        fetchPolicy: 'no-cache',
        query: generateCaptionFeed,
        variables: {
          page,
          search,
          liked,
          categories,
          sort,
          hideUsed,
          items: 15,
          caption
        }
      })

      const captionList = data?.generateCaptionFeed?.docs || []
      const total = data?.generateCaptionFeed?.totalDocs || 0

      dispatch(setCaptions(uniqBy([...captions, ...captionList], '_id')))
      dispatch(changeCaptionsTotalDocs(total))
      handleSearchTrack(total)
    } catch (err) {
      console.log(err)
      toast.error((err as any).message)
    }
  }

  useEffect(() => {
    if (loading) {
      loadCaptions()
    }
  }, [loading])

  useEffect(() => {
    dispatch(setCaptionsQueryState(getSearchFromURL()))
  }, [])

  return (
    <StyledCaptionList>
      {!loading && !captions.length && (
        <EmptyState
          title="We came up empty"
          body="We couldn't find a caption that matches that query. How about a different search?"
        />
      )}
      {loading && page === 0 && (
        <Masonry
          className="masonry-grid"
          columnClassName="masonry-grid-column"
          breakpointCols={{
            default: 2,
            900: 1
          }}
        >
          {times(15, (num) => {
            return (
              <Skeleton
                randHeight={[280, 480]}
                styles="&:not(:last-child) {
                      margin-bottom: 10px;
                    }"
                key={num}
              />
            )
          })}
        </Masonry>
      )}
      <InfiniteScroll
        scrollThreshold={0.7}
        dataLength={captions.length}
        next={() => dispatch(changeCaptionsPage(page + 1))}
        hasMore={captions.length < totalDocs}
        loader={null}
        {...conditionalStage(isMobile(), { scrollableTarget: 'app-content' })}
      >
        <Masonry
          className="masonry-grid"
          columnClassName="masonry-grid-column"
          breakpointCols={{
            default: 2,
            900: 1
          }}
        >
          {captions.map((caption, index) => {
            return <CaptionListItem caption={caption} key={`${caption._id}-${index}`} />
          })}
        </Masonry>
      </InfiniteScroll>
    </StyledCaptionList>
  )
}

export default CaptionList
